import { useMemo } from 'react'
import posthog from 'posthog-js'
import { PostHogProvider } from 'posthog-js/react'
import { getPostHogApiHost } from '@percent/utility'
import { AnalyticsContext } from '../AnalyticsContext/AnalyticsContext'
import { env } from '@percent/cause-dashboard/env'

import type { AnalyticsContextControllerProps } from './AnalyticsContextController.types'

let posthogInitialized = false

/** @internal Exported for test cleanup only */
export const __resetPostHogInitForTests = () => {
  posthogInitialized = false
}

export const AnalyticsContextController = ({
  writeKey,
  logger,
  options,
  children
}: AnalyticsContextControllerProps) => {
  const value = useMemo(() => {
    try {
      if (env.NX_PUBLIC_POSTHOG_API_KEY && !posthogInitialized) {
        posthog.init(env.NX_PUBLIC_POSTHOG_API_KEY, {
          api_host: getPostHogApiHost(window.location.hostname),
          ui_host: 'https://eu.posthog.com',
          capture_pageview: false,
          person_profiles: 'identified_only',
          session_recording: {
            sampleRate: 0.1
          }
        })
        posthog.register({ app: 'causes-portal' })
        posthogInitialized = true
      }

      if (!writeKey) return {}

      const proxyOptions =
        env.NX_PUBLIC_USE_SEGMENT_PROXY === true
          ? {
              integrations: {
                'Segment.io': {
                  apiHost: `${window.location.host}/gs-ev/v1`,
                  protocol: 'https'
                }
              }
            }
          : {}

      window?.analytics.load(writeKey, { ...options, ...proxyOptions })

      return { analytics: window?.analytics }
    } catch (error) {
      logger(error)
      return {}
    }
  }, [writeKey, options])

  const content = <AnalyticsContext.Provider value={value}>{children}</AnalyticsContext.Provider>

  if (env.NX_PUBLIC_POSTHOG_API_KEY) {
    return <PostHogProvider client={posthog}>{content}</PostHogProvider>
  }

  return content
}
